<script setup>
const props = defineProps({
  modelValue: { type: String, default: "" },
  length: { type: Number, default: 6 },
  disabled: Boolean,
})

const emit = defineEmits(["update:modelValue", "complete"])
const inputs = ref([])
const digits = ref(Array.from({ length: props.length }, () => ""))

const normalize = value => String(value ?? "")
  .replace(/[۰-۹]/g, digit => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
  .replace(/[٠-٩]/g, digit => "٠١٢٣٤٥٦٧٨٩".indexOf(digit))
  .replace(/\D/g, "")
  .slice(0, props.length)

const syncFromModel = value => {
  const normalized = normalize(value)
  digits.value = Array.from(
    { length: props.length },
    (_, index) => normalized[index] || "",
  )
}

const publish = () => {
  const value = digits.value.join("")
  emit("update:modelValue", value)
  if (value.length === props.length) emit("complete", value)
}

const focusAt = index => {
  nextTick(() => inputs.value[index]?.focus())
}

const setDigits = (value, startIndex = 0) => {
  const normalized = normalize(value)
  if (!normalized) return

  normalized.split("").forEach((digit, offset) => {
    const index = startIndex + offset
    if (index < props.length) digits.value[index] = digit
  })
  publish()
  focusAt(Math.min(startIndex + normalized.length, props.length - 1))
}

const handleInput = (event, index) => {
  const value = normalize(event.target.value)
  event.target.value = value.slice(-1)
  if (!value) {
    digits.value[index] = ""
    publish()
    return
  }

  setDigits(value, index)
  if (value.length === 1 && index < props.length - 1) focusAt(index + 1)
}

const handleKeydown = (event, index) => {
  if (event.key === "Backspace") {
    event.preventDefault()
    if (digits.value[index]) digits.value[index] = ""
    else if (index > 0) {
      digits.value[index - 1] = ""
      focusAt(index - 1)
    }
    publish()
    return
  }

  if (event.key === "ArrowLeft" && index > 0) focusAt(index - 1)
  if (event.key === "ArrowRight" && index < props.length - 1) focusAt(index + 1)
  if (event.key.length === 1 && !/[0-9۰-۹٠-٩]/.test(event.key)) event.preventDefault()
}

const handlePaste = event => {
  event.preventDefault()
  const value = normalize(event.clipboardData?.getData("text"))
  if (!value) return
  digits.value.fill("")
  setDigits(value)
}

watch(() => props.modelValue, value => {
  if (normalize(value) !== digits.value.join("")) syncFromModel(value)
}, { immediate: true })
</script>

<template>
  <div
    class="otp-code"
    dir="ltr"
    :style="{ '--otp-length': props.length }"
    @paste="handlePaste"
  >
    <input
      v-for="(_, index) in digits"
      :key="index"
      :ref="element => inputs[index] = element"
      :value="digits[index]"
      :disabled="disabled"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      maxlength="1"
      :autocomplete="index === 0 ? 'one-time-code' : 'off'"
      :enterkeyhint="index === props.length - 1 ? 'done' : 'next'"
      :autofocus="index === 0"
      autocapitalize="off"
      :spellcheck="false"
      :aria-label="`رقم ${index + 1} کد تأیید`"
      @input="handleInput($event, index)"
      @keydown="handleKeydown($event, index)"
      @focus="$event.target.select()"
    >
  </div>
</template>

<style scoped>
.otp-code{display:grid;width:min(100%,328px);grid-template-columns:repeat(var(--otp-length),minmax(0,48px));direction:ltr;justify-content:center;gap:8px;margin-inline:auto}.otp-code input{box-sizing:border-box;width:48px;height:52px;border:1px solid #d9d5e1;border-radius:13px;outline:0;color:#352e47;background:#fff;font-family:inherit;font-size:20px;font-weight:800;line-height:52px;text-align:center;caret-color:rgb(var(--v-theme-primary));transition:border-color .16s,box-shadow .16s,background .16s}.otp-code input:hover{border-color:#aaa1bc}.otp-code input:focus{border-color:rgb(var(--v-theme-primary));background:rgba(var(--v-theme-primary),.025);box-shadow:0 0 0 3px rgba(var(--v-theme-primary),.12)}.otp-code input:disabled{opacity:.65;cursor:not-allowed}@media(max-width:390px){.otp-code{width:100%;grid-template-columns:repeat(var(--otp-length),minmax(0,42px));gap:6px}.otp-code input{width:42px;height:48px;border-radius:11px;font-size:18px;line-height:48px}}@media(max-width:330px){.otp-code{grid-template-columns:repeat(var(--otp-length),minmax(0,38px));gap:5px}.otp-code input{width:38px;height:45px}}
</style>
